import { ChangeEvent, ComponentProps, useCallback, useRef } from 'react';

import { ClearButton } from 'components/Button';
import { Input } from 'components/Input/Input';
import { VisuallyHidden } from 'components/VisuallyHidden';
import { matchSize } from 'utils/responsive';
import { truncateToUTF8Bytes } from 'utils/string/truncateToUTF8Bytes';

type Props = {
  placeholder?: string;
  xsPlaceholder?: string;
  initialQuery: string;
  onChange: (value: string) => void;
  onClearQuery: () => void;
  presentation: ComponentProps<typeof Input>['styleVariant'];
  id: string;
  qaId: string;
};

export function UncontrolledSearchQueryInputContainer({
  placeholder,
  xsPlaceholder,
  initialQuery,
  onChange,
  onClearQuery,
  presentation,
  id,
  qaId,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const clearQuery = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    onClearQuery();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onClearQuery]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const eventValue = event.target.value;
      const value = truncateToUTF8Bytes(eventValue, 512);

      if (inputRef.current && value !== eventValue) {
        inputRef.current.value = value;
      }

      onChange(value);
    },
    [onChange],
  );

  return (
    <>
      <VisuallyHidden>
        <label htmlFor={id}>{placeholder}</label>
      </VisuallyHidden>
      <Input
        key={initialQuery}
        inputProps={{
          ref: inputRef,
          id,
          name: 'q',
          placeholder: matchSize({ xs: true }) ? xsPlaceholder : placeholder,
          defaultValue: initialQuery,
          onChange: handleChange,
          qaId,
          title: placeholder,
        }}
        suffix={
          <ClearButton
            $visible
            onClick={clearQuery}
            data-qa-id="search-input-clear"
            aria-label={getText('Clear search input')}
          />
        }
        hideSuffixWhenEmpty
        styleVariant={presentation}
        affixVariant="transparent"
        fullHeight
      />
    </>
  );
}
